import { memo } from "react";
import { RichTooltip } from "@/components/common/RichTooltip";
import { contextUsagePercent, formatContextUsageTooltip, type ContextUsage } from "@/lib/chat";

const SIZE = 16;
const STROKE = 2.5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// 使用率がこの値を超えたら色を段階的に強める。
const WARNING_PERCENT = 75;
const DANGER_PERCENT = 90;

function meterColor(percent: number): string {
	if (percent >= DANGER_PERCENT) return "var(--danger)";
	if (percent >= WARNING_PERCENT) return "var(--warning)";
	return "var(--fg-3)";
}

/**
 * 直近のコンテキスト使用率を円グラフで示す。ホバーで使用トークン数と上限を出す。
 */
export const ContextUsageMeter = memo(function ContextUsageMeter({
	usage,
}: {
	usage: ContextUsage;
}) {
	const percent = contextUsagePercent(usage);
	const color = meterColor(percent);

	return (
		<RichTooltip
			side="top"
			content={
				<span style={{ whiteSpace: "nowrap" }}>
					コンテキスト {percent}%
					<br />
					{formatContextUsageTooltip(usage)}
				</span>
			}
		>
			<span
				data-testid="composer-context-meter"
				data-percent={percent}
				style={{ display: "inline-flex", padding: 4, flexShrink: 0 }}
			>
				<svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} fill="none">
					<circle
						cx={SIZE / 2}
						cy={SIZE / 2}
						r={RADIUS}
						stroke="var(--line-1)"
						strokeWidth={STROKE}
					/>
					{/* 12 時方向を起点に時計回りへ伸ばすため、円を -90 度回す。
					    0% では長さ 0 の破線が丸い線端のせいで点として描かれるため、弧自体を出さない。 */}
					{percent > 0 && (
						<circle
							cx={SIZE / 2}
							cy={SIZE / 2}
							r={RADIUS}
							stroke={color}
							strokeWidth={STROKE}
							strokeLinecap="round"
							strokeDasharray={`${(CIRCUMFERENCE * percent) / 100} ${CIRCUMFERENCE}`}
							transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
						/>
					)}
				</svg>
			</span>
		</RichTooltip>
	);
});
